import React, { useEffect, useState } from 'react';
import useBackendApi from '../../hooks/useBackendApi';
import { Box, SxProps, Tooltip } from '@mui/material';
import GradientCircularProgress from './GradientCircularProgress';
import DOMPurify from 'dompurify';
import { useThemeContext } from '../../contexts/BaseThemeContextProvider';

const backendURL = process.env.REACT_APP_BACKEND_URL;

type validTooltipPlacement =
  | 'bottom'
  | 'left'
  | 'right'
  | 'top'
  | 'bottom-end'
  | 'bottom-start'
  | 'left-end'
  | 'left-start'
  | 'right-end'
  | 'right-start'
  | 'top-end'
  | 'top-start';

type TooltipFromDBProps = {
  helpId: string;
  tooltipPlacement?: validTooltipPlacement;
  imageSxProps?: SxProps;
  additionalCircularSxProps?: SxProps;
  circularContainerSxProps?: SxProps;
};

const TooltipFromDB: React.FC<TooltipFromDBProps> = ({
  tooltipPlacement = 'top',
  helpId,
  imageSxProps = {},
  additionalCircularSxProps = {},
  circularContainerSxProps = { display: 'flex' },
}) => {
  const { theme } = useThemeContext();
  const [helpResponse, setHelpResponse] = useState('');
  const { apiFetchRequest } = useBackendApi();
  const [isFetching, setIsFetching] = useState(true);
  const { addAlertMessages } = useThemeContext();

  useEffect(() => {
    const fetchHelpIdDetails = async () => {
      const helpResponse = await apiFetchRequest({ endpoint: `${backendURL}/help/${helpId}` });
      if (helpResponse.status !== 200) {
        addAlertMessages({ message: 'Error fetching details for tooltip', severityType: 'error' });
        return;
      }
      const sanitizedHtml = DOMPurify.sanitize(helpResponse.body?.h);
      setHelpResponse(sanitizedHtml || helpId);
      setIsFetching(false);
    };
    fetchHelpIdDetails();
  }, [helpId]);

  if (isFetching) {
    return (
      <GradientCircularProgress
        loadingMessage=""
        additionalCircularSxProps={{
          width: '30px !important',
          height: '30px !important',
          mr: '10px',
          ...additionalCircularSxProps,
        }}
        containerSxProps={{ ...circularContainerSxProps }}
      />
    );
  }

  return (
    <Tooltip
      arrow
      placement={tooltipPlacement}
      enterTouchDelay={0}
      leaveTouchDelay={5000}
      onClick={(e) => e.preventDefault()}
      title={<div dangerouslySetInnerHTML={{ __html: helpResponse }} />}
      componentsProps={{
        tooltip: {
          sx: {
            color: theme.custom.components.toolTipFromDB.tooltip.color,
            backgroundColor: theme.custom.components.toolTipFromDB.tooltip.backgroundColor,
            boxShadow: theme.custom.components.toolTipFromDB.tooltip.boxShadowColor,
            padding: '2px 18px',
            '& .MuiTooltip-arrow': {
              fontSize: '26px',
              color: theme.custom.components.toolTipFromDB.tooltip.arrow.color,
            },
          },
        },
      }}
    >
      <Box component="img" src={theme.custom.components.toolTipFromDB.images.src} sx={{ ...imageSxProps }} />
    </Tooltip>
  );
};

export default TooltipFromDB;
